/* The room videos (site-furniture spec §5.4), for every <video data-gr-video> on the page:
   - "visible" (the home reels and the product page's In the room band): loads when about to be seen, plays muted while at least half
     in view, pauses when it leaves; its button pauses it and keeps it paused until pressed again;
   - "gallery" (the product gallery's room slide): the same on a desktop; on a phone it waits for the shopper's first touch, scroll or key, then plays once at least half in view (31 Oct
     2026: it downloaded 4.7 MB as an ad visitor arrived, while the theme was still settling the gallery on its first
     picture);
   - "card" (collection cards): on a desktop, plays on hover or focus and stops when left; on a phone, only the one
     card most in view (at least 60%) plays, from the visitor's first scroll (23 Oct 2026: the first row is on a
     phone's first screen now, and a card's clip shouldn't download before anyone browses).
   With reduced motion or Save-Data nothing plays by itself: the gallery and the reels show their poster, and their
   button plays them. Cards and reels added to the page later are picked up too. */
(() => {
  if (window.grVideo) return;
  window.grVideo = true;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches || !!navigator.connection?.saveData;
  const hover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const load = (v) => {
    if (v.dataset.grLoaded) return;
    v.dataset.grLoaded = '1';
    v.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; });
    v.load();
  };
  const play = (v) => {
    load(v);
    const p = v.play();
    if (p && p.catch) p.catch(() => {});                 // a browser may refuse; the poster or image stays
  };
  const stop = (v) => { if (!v.paused) v.pause(); };
  const button = (v) => v.closest('.gr-room-slide__frame, .gr-reel, .gr-clip')?.querySelector('[data-gr-video-toggle]');
  const label = (v, paused) => {
    const b = button(v);
    if (!b) return;
    b.setAttribute('aria-pressed', String(paused));
    const named = paused ? b.dataset.labelPlay : b.dataset.labelPause;   // both labels come from the theme's language
    b.setAttribute('aria-label', named || b.getAttribute('aria-label').replace(/^(Pause|Play)/, paused ? 'Play' : 'Pause'));
  };

  const near = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting && !still) { load(e.target); near.unobserve(e.target); } });
  }, { rootMargin: '300px 0px' });
  const inView = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const v = e.target;
      if (e.isIntersecting && e.intersectionRatio >= 0.5) {
        if (!still && !v.dataset.grHeld) play(v);
      } else {
        stop(v);
      }
    });
  }, { threshold: [0, 0.5] });

  // A phone's cards wait for the first scroll (or a page that opens part way down)
  let scrolled = window.scrollY > 0;
  const waiting = [];
  const afterScroll = (fn) => (scrolled ? fn() : waiting.push(fn));
  if (!scrolled) {
    window.addEventListener('scroll', () => { scrolled = true; waiting.splice(0).forEach((fn) => fn()); }, { passive: true, once: true });
  }

  // A phone's gallery clip waits for the first touch, scroll or key (swiping the gallery starts with a touch)
  let touched = scrolled;
  const touchWaiting = [];
  const afterTouch = (fn) => (touched ? fn() : touchWaiting.push(fn));
  const firstTouch = () => {
    if (touched) return;
    touched = true;
    touchWaiting.splice(0).forEach((fn) => fn());
  };
  if (!touched) {
    ['touchstart', 'pointerdown', 'scroll', 'wheel', 'keydown'].forEach((type) => {
      window.addEventListener(type, firstTouch, { passive: true, once: true });
    });
  }

  const ratios = new Map();
  const phone = new IntersectionObserver((entries) => {
    entries.forEach((e) => ratios.set(e.target, e.isIntersecting ? e.intersectionRatio : 0));
    let best = null, top = 0.6;
    ratios.forEach((r, v) => { if (r >= top) { best = v; top = r; } });
    ratios.forEach((r, v) => (v === best ? play(v) : stop(v)));
  }, { threshold: [0, 0.25, 0.5, 0.6, 0.75, 0.9, 1] });

  function setup(v) {
    if (v.dataset.grReady) return;
    v.dataset.grReady = '1';
    v.addEventListener('playing', () => v.classList.add('is-playing'));
    v.addEventListener('pause', () => v.classList.remove('is-playing'));
    if (v.dataset.grVideo === 'card') {
      if (still) return;
      if (!hover) { afterScroll(() => phone.observe(v)); return; }
      const host = v.closest('.product-card') || v.parentElement;
      host.addEventListener('mouseenter', () => play(v));
      host.addEventListener('mouseleave', () => stop(v));
      host.addEventListener('focusin', () => play(v));
      host.addEventListener('focusout', () => stop(v));
      return;
    }
    if (still) label(v, true);
    if (v.dataset.grVideo === 'gallery' && !hover) {
      if (!still) afterTouch(() => inView.observe(v));
      return;
    }
    near.observe(v);
    inView.observe(v);
  }

  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-gr-video-toggle]');
    const v = b?.parentElement.querySelector('video[data-gr-video]');
    if (!v) return;
    e.preventDefault();
    if (v.paused) {
      delete v.dataset.grHeld;
      play(v);
      label(v, false);
    } else {
      v.dataset.grHeld = '1';
      stop(v);
      label(v, true);
    }
  });

  const scan = (root) => root.querySelectorAll('video[data-gr-video]').forEach(setup);
  scan(document);
  new MutationObserver((changes) => changes.forEach((c) => c.addedNodes.forEach((n) => {
    if (n.nodeType !== 1) return;
    if (n.matches('video[data-gr-video]')) setup(n);
    else scan(n);
  }))).observe(document.body, { childList: true, subtree: true });
})();
